import { ApplicationRef, DestroyRef, DOCUMENT, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

/**
 * Trạng thái lời nhắc đang cần hiển thị cho người dùng.
 * - `none`   — không có gì.
 * - `ready`  — ngsw đã TẢI XONG một phiên bản mới, chỉ chờ tải lại trang để chạy nó.
 * - `broken` — ngsw rơi vào trạng thái không cứu được, buộc phải tải lại.
 */
export type AppUpdateState = 'none' | 'ready' | 'broken';

/**
 * Chu kỳ hỏi máy chủ xem có bản mới chưa. ngsw đã tự hỏi mỗi lần service worker khởi
 * động, nên mốc này chỉ để lo cho những phiên mở liên tục nhiều giờ — đúng kiểu dùng
 * của một ERP nội bộ (mở tab từ sáng tới chiều, hoặc PWA không bao giờ bị thoát hẳn).
 */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Cầu nối giữa `SwUpdate` của ngsw và một lời nhắc "Đã có bản mới · Tải lại".
 *
 * Vì sao cần: `ngsw-config.json` để `index.html` + `*.js` + `*.css` ở `installMode:
 * prefetch`, nên sau mỗi lần deploy service worker vẫn tự tải trọn vỏ app mới về nền —
 * NHƯNG ngsw ghim mỗi client vào đúng phiên bản nó khởi động cùng. Không ai gọi
 * `activateUpdate()` thì bản mới chỉ có hiệu lực khi người dùng đóng hẳn app rồi mở
 * lại, và thường phải hai lần (lần đầu phát hiện + tải, lần sau mới chạy). Với một PWA
 * đã cài, "đóng hẳn" gần như không bao giờ xảy ra ⇒ người dùng chạy code cũ nhiều ngày.
 *
 * Sự kiện `unrecoverable` là mặt còn lại của cùng vấn đề: khi deploy mới xoá đi những
 * tệp băm mà client hiện tại còn tham chiếu, ngsw không thể phục vụ tiếp và báo hỏng.
 * Không xử lý thì màn hình treo/trắng cho tới khi người dùng tự nghĩ ra việc tải lại.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  private readonly _state = signal<AppUpdateState>('none');
  readonly state = this._state.asReadonly();

  /** Gọi một lần lúc khởi động app (xem `provideAppInitializer` trong app.config.ts). */
  init(): void {
    // Tắt ở chế độ dev và ở trình duyệt không có service worker — `isEnabled` gói cả hai.
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((event) => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this._state.set('ready'));

    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._state.set('broken'));

    // Chờ app ổn định RỒI mới bật bộ đếm: `interval` chạy trong zone, nếu khởi động ngay
    // thì `ApplicationRef.isStable` không bao giờ về true nữa và mọi thứ chờ tín hiệu đó
    // sẽ treo. `concat` cũng cho ta một lần kiểm tra ngay khi app vừa ổn định.
    concat(this.appRef.isStable.pipe(first((isStable) => isStable)), interval(CHECK_INTERVAL_MS))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.swUpdate.checkForUpdate().catch(() => undefined));
  }

  /** Bỏ qua lời nhắc bản mới. Trạng thái `broken` KHÔNG bỏ qua được — app đang hỏng thật. */
  dismiss(): void {
    if (this._state() === 'ready') {
      this._state.set('none');
    }
  }

  /**
   * Kích hoạt phiên bản đã tải rồi tải lại trang.
   * `activateUpdate()` có thể lỗi (rõ nhất là ở trạng thái `broken`, khi chẳng còn phiên
   * bản nào lành để kích hoạt) — vẫn tải lại, vì chính việc tải lại mới là thứ gỡ bế tắc.
   */
  async reload(): Promise<void> {
    await this.swUpdate.activateUpdate().catch(() => undefined);
    this.document.location.reload();
  }
}
